import React, { useState } from "react";
import NoteAddIcon from "@mui/icons-material/NoteAdd";
import IconButton from "@mui/material/IconButton";
import DialogExportEVA from "../dialogs/DialogExportEVA";
import { CalculateStaticAnalysisWithPeriod } from "../operations/AnalysisOperations";
import { useSnackbar } from "notistack";
import { gapi } from "gapi-script";
import ProjectAnalysis from "../model/ProjectAnalysis";
import { Snackbar } from "@mui/material";
import Alert from "@mui/material/Alert";
import { Tooltip } from "@mui/material";

import loadGoogleClient from "./LoadGoogleClient";

const createSheets = (valueToBeWritten, sheetCreated, period, enqueueSnackbar) => {
  let date = new Date();

  let newFileName =
    " EVA Export for Period: " + period + ",  " +
    date.toLocaleTimeString() +
    (date.getFullYear() + "-" + (date.getMonth() + 1) + "-" + date.getDate());

  let name_spreadsheetID_ToCopy = "smaeva_export_template";
  findSpreadsheetID(
    newFileName,
    name_spreadsheetID_ToCopy,
    valueToBeWritten,
    createSheetUsingTemplate,
    sheetCreated,
    enqueueSnackbar
  );
};

const findSpreadsheetID = (
  newFileName,
  name_spreadsheetID_ToFind,
  valueToBeWritten,
  resultHandler,
  sheetCreated,
  enqueueSnackbar
) => {
  try {
    gapi.client.drive.files
      .list({
        q:
          "mimeType = 'application/vnd.google-apps.spreadsheet' and name = '" +
          name_spreadsheetID_ToFind +
          "'",
      })
      .then((response) => {
        resultHandler(
          newFileName,
          response.result.files[0].id,
          valueToBeWritten,
          sheetCreated,
          enqueueSnackbar
        );
      }, (err) => {
        if (err.status === 401) {
          enqueueSnackbar(<div>Your google login has expired.  Please save any work if necessary and click here to log in again: <a href="/.auth/logout">Logout</a></div>, { variant: "error" });
        }
      });
  } catch (err) {
    enqueueSnackbar(err.message, { variant: "error" });
    return;
  }
};

const createSheetUsingTemplate = (
  newFileName,
  spreadsheetID_found,
  valueToBeWritten,
  sheetCreated,
  enqueueSnackbar
) => {
  try {
    gapi.client.drive.files
      .copy({
        fileId: spreadsheetID_found,
        resource: { name: newFileName },
      })
      .then((resp) => {
        sheetCreated(true);
        updateSheet("", resp.result.id, valueToBeWritten, enqueueSnackbar);
      });
  } catch (err) {
    enqueueSnackbar(err.message, { variant: "error" });
    return;
  }
};

const updateSheet = (
  newFileName,
  spreadsheetID_found,
  valueToBeWritten,
  enqueueSnackbar
) => {
  updateValues(
    spreadsheetID_found,
    "A5:P",
    "USER_ENTERED",
    valueToBeWritten,
    enqueueSnackbar
  );
};

const updateValues = (
  spreadsheetId,
  range,
  valueInputOption,
  _values,
  enqueueSnackbar
) => {
  let values = _values.map(row => {
    const googleRow = [];
    googleRow[0] = row.displayName;
    googleRow[1] = row.progress;
    googleRow[2] = row.budgetSpent;
    googleRow[3] = row.plannedProgress;
    googleRow[4] = row.bac;
    googleRow[5] = row.actualCost;
    googleRow[6] = row.actualCostPeriod;
    googleRow[7] = row.cpiCumulative;
    googleRow[8] = row.spiCumulative;
    googleRow[9] = row.fac;
    googleRow[10] = row.vac;
    googleRow[11] = row.costStatusIndicator;
    googleRow[12] = row.schedStatusIndicator;
    googleRow[13] = row.comment;
    return googleRow;
  });
  const body = {
    values: values,
  };
  try {
    gapi.client.sheets.spreadsheets.values
      .update({
        spreadsheetId: spreadsheetId,
        range: range,
        valueInputOption: valueInputOption,
        resource: body,
      })
      .then((response) => {
        addStyle(spreadsheetId, _values, styleIndividualRow, enqueueSnackbar);
      });
  } catch (err) {
    enqueueSnackbar(err.message, { variant: "error" });
    return;
  }
};

const addStyle = (spreadsheetId, value, resultHandler, enqueueSnackbar) => {
  let requests = [];
  let data = value;
  let styleInfo = value.map(e => e.styles);
  for (let i = 0; i < styleInfo.length; i++) {
    let colorCPI = {};
    let colorSPI = {};
    let colorCPIStatus = {};
    let colorScheduleStatus = {};

    if (data[i].cpiCumulativeIcon === "\u25B2") {
      colorCPI = {
        red: 0 / 255,
        green: 176 / 255,
        blue: 80 / 255,
        alpha: 1,
      };
    } else if (data[i].cpiCumulativeIcon === "\u25AC") {
      colorCPI = {
        red: 250 / 255,
        green: 170 / 255,
        blue: 15 / 255,
        alpha: 1,
      };
    } else {
      colorCPI = {
        red: 255 / 255,
        green: 0 / 255,
        blue: 0 / 255,
        alpha: 1,
      };
    }
    if (data[i].spiCumulativeIcon === "\u25B2") {
      colorSPI = {
        red: 0 / 255,
        green: 176 / 255,
        blue: 80 / 255,
        alpha: 1,
      };
    } else if (data[i].spiCumulativeIcon === "\u25AC") {
      colorSPI = {
        red: 250 / 255,
        green: 170 / 255,
        blue: 15 / 255,
        alpha: 1,
      };
    } else {
      colorSPI = {
        red: 255 / 255,
        green: 0 / 255,
        blue: 0 / 255,
        alpha: 1,
      };
    }
    // Defining the coulour of CPI Status; not sure about the values that are on the sheet. So, multiplying the comparision value by 100, double check it with Matt
    if (data[i].cpiCumulative >= 1.0) {
      colorCPIStatus = {
        red: 0 / 255,
        green: 176 / 255,
        blue: 80 / 255,
        alpha: 1,
      };
    } else if (data[i].cpiCumulative >= 0.65) {
      colorCPIStatus = {
        red: 255 / 255,
        green: 255 / 255,
        blue: 0 / 255,
        alpha: 1,
      };
    } else if (data[i].cpiCumulative >= 0.4) {
      colorCPIStatus = {
        red: 255 / 255,
        green: 0 / 255,
        blue: 0 / 255,
        alpha: 1,
      };
    } else if (data[i].cpiCumulative > 0) {
      colorCPIStatus = {
        red: 0 / 255,
        green: 0 / 255,
        blue: 0 / 255,
        alpha: 1,
      };
    } else if (data[i].cpiCumulative === 0) {
      colorCPIStatus = {
        red: 169 / 255,
        green: 169 / 255,
        blue: 169 / 255,
        alpha: 1,
      };
    }
    // Defining the coulour of Schedule Status; not sure about the values that are on the sheet. So, multiplying the comparision value by 100, double check it with Matt
    if (data[i].spiCumulative >= 1.0) {
      colorScheduleStatus = {
        red: 0 / 255,
        green: 176 / 255,
        blue: 80 / 255,
        alpha: 1,
      };
    } else if (data[i].spiCumulative >= 0.65) {
      colorScheduleStatus = {
        red: 255 / 255,
        green: 255 / 255,
        blue: 0 / 255,
        alpha: 1,
      };
    } else if (data[i].spiCumulative >= 0.4) {
      colorScheduleStatus = {
        red: 255 / 255,
        green: 0 / 255,
        blue: 0 / 255,
        alpha: 1,
      };
    } else if (data[i].spiCumulative > 0) {
      colorScheduleStatus = {
        red: 0 / 255,
        green: 0 / 255,
        blue: 0 / 255,
        alpha: 1,
      };
    } else if (data[i].spiCumulative === 0) {
      colorScheduleStatus = {
        red: 169 / 255,
        green: 169 / 255,
        blue: 169 / 255,
        alpha: 1,
      };
    }

    requests.push(
      {
        repeatCell: {
          range: {
            sheetId: 0,
            startRowIndex: i + 4,
            endRowIndex: i + 5,
            startColumnIndex: 0,
            endColumnIndex: 11,
          },
          cell: {
            userEnteredFormat: {
              textFormat: {
                fontSize: styleInfo[i].fontSize,
                bold: styleInfo[i].fontStyle === "bold" ? true : false,
                italic: styleInfo[i].fontStyle === "italic" ? true : false,
              },
              horizontalAlignment: "LEFT",
            },
          },
          fields: "userEnteredFormat.textFormat,userEnteredFormat.horizontalAlignment",
        },
      },
      {
        repeatCell: {
          range: {
            sheetId: 0,
            startRowIndex: i + 4,
            startColumnIndex: 4,
            endColumnIndex: 7,
          },
          cell: {
            userEnteredFormat: {
              numberFormat: {
                type: "CURRENCY",
                pattern: "\"$\"#,##0.00",
              },
            },
          },
          fields: "userEnteredFormat.numberFormat",
        },
      },
      {
        repeatCell: {
          range: {
            sheetId: 0,
            startRowIndex: i + 4,
            startColumnIndex: 9,
            endColumnIndex: 11,
          },
          cell: {
            userEnteredFormat: {
              numberFormat: {
                type: "CURRENCY",
                pattern: "\"$\"#,##0.00",
              },
            },
          },
          fields: "userEnteredFormat.numberFormat",
        },
      },
      //commented out until dynamic formatting based on template layout is in place, as these fields are not present in the current template
      /*{
        repeatCell: {
          range: {
            sheetId: 0,
            startRowIndex: i + 4,
            endRowIndex: i + 5,
            startColumnIndex: 7,
            endColumnIndex: 8,
          },
          cell: {
            userEnteredFormat: {
              textFormat: {
                foregroundColorStyle: { rgbColor: colorCPI },
              },
            },
          },
          fields: "userEnteredFormat.textFormat",
        },
      },
      {
        repeatCell: {
          range: {
            sheetId: 0,
            startRowIndex: i + 4,
            endRowIndex: i + 5,
            startColumnIndex: 8,
            endColumnIndex: 9,
          },
          cell: {
            userEnteredFormat: {
              textFormat: {
                fontSize: styleInfo[i].fontSize,
                bold: styleInfo[i].fontStyle === "bold" ? true : false,
                italic: styleInfo[i].fontStyle === "italic" ? true : false,
              },
            },
          },
          fields: "userEnteredFormat.textFormat",
        },
      },
      {
        repeatCell: {
          range: {
            sheetId: 0,
            startRowIndex: i + 4,
            endRowIndex: i + 5,
            startColumnIndex: 9,
            endColumnIndex: 10,
          },
          cell: {
            userEnteredFormat: {
              textFormat: {
                foregroundColorStyle: { rgbColor: colorSPI },
              },
            },
          },
          fields: "userEnteredFormat.textFormat",
        },
      },
      {
        repeatCell: {
          range: {
            sheetId: 0,
            startRowIndex: i + 1,
            endRowIndex: i + 2,
            startColumnIndex: 10,
            endColumnIndex: 13,
          },
          cell: {
            userEnteredFormat: {
              textFormat: {
                fontSize: styleInfo[i][1],
                bold: styleInfo[i][2] === "bold" ? true : false,
                italic: styleInfo[i][2] === "italic" ? true : false,
              },
            },
          },
          fields: "userEnteredFormat.textFormat",
        },
      },*/
      { //cost status color indicator column
        repeatCell: {
          range: {
            sheetId: 0,
            startRowIndex: i + 4,
            endRowIndex: i + 5,
            startColumnIndex: 11,
            endColumnIndex: 12,
          },
          cell: {
            userEnteredFormat: {
              textFormat: {
                foregroundColorStyle: { rgbColor: colorCPIStatus },
              },
            },
          },
          fields: "userEnteredFormat.textFormat",
        },
      },
      { //sched status color indicator column
        repeatCell: {
          range: {
            sheetId: 0,
            startRowIndex: i + 4,
            endRowIndex: i + 5,
            startColumnIndex: 12,
            endColumnIndex: 13,
          },
          cell: {
            userEnteredFormat: {
              textFormat: {
                foregroundColorStyle: { rgbColor: colorScheduleStatus },
              },
            },
          },
          fields: "userEnteredFormat.textFormat",
        },
      },
      { //notes column
        repeatCell: {
          range: {
            sheetId: 0,
            startRowIndex: i + 4,
            endRowIndex: i + 5,
            startColumnIndex: 13,
            endColumnIndex: 14,
          },
          cell: {
            userEnteredFormat: {
              textFormat: {
                fontSize: styleInfo[i].fontSize,
                bold: styleInfo[i].fontStyle === "bold" ? true : false,
                italic: styleInfo[i].fontStyle === "italic" ? true : false,
              },
            },
          },
          fields: "userEnteredFormat.textFormat",
        },
      }
    );
    const childCount = styleInfo[i].childCount;
      if (childCount > 0) {
        requests.push({
          addDimensionGroup: {
            range: {
              sheetId: 0,
              dimension: "ROWS",
              startIndex: i + 5,
              endIndex: i + 5 + childCount
            }
          }
        });
      }
  }
      
  requests.push({
    updateSheetProperties: {
      properties: {
        sheetId: 0,
        gridProperties: {
          frozenRowCount: 4,
        },
      },
      fields: "gridProperties.frozenRowCount",
    },
  });
  resultHandler(spreadsheetId, requests, autoResizeRows, enqueueSnackbar);
};

const styleIndividualRow = (
  spreadsheetId,
  requests,
  resultHandler,
  enqueueSnackbar
) => {
  try {
    const batchUpdateRequest = { requests: requests };
    gapi.client.sheets.spreadsheets
      .batchUpdate({
        spreadsheetId: spreadsheetId,
        resource: batchUpdateRequest,
      })
      .then(() => {
        resultHandler(spreadsheetId, enqueueSnackbar);
      });
  } catch (err) {
    enqueueSnackbar(err.message, { variant: "error" });
    return;
  }
};

const autoResizeColumns = (spreadsheetId, resultHandler, enqueueSnackbar) => {
  const requests = [];
  requests.push({
    autoResizeDimensions: {
      dimensions: {
        sheetId: 0,
        dimension: "COLUMNS",
      },
    },
  });
  try {
    const batchUpdateRequest = { requests: requests };
    gapi.client.sheets.spreadsheets
      .batchUpdate({
        spreadsheetId: spreadsheetId,
        resource: batchUpdateRequest,
      })
      .then(() => {
        resultHandler(spreadsheetId, enqueueSnackbar);
      });
  } catch (err) {
    enqueueSnackbar(err.message, { variant: "error" });
    return;
  }
};

const autoResizeRows = (spreadsheetId, enqueueSnackbar) => {
  const requests = [];
  requests.push({
    autoResizeDimensions: {
      dimensions: {
        sheetId: 0,
        dimension: "ROWS",
      },
    },
  });
  try {
    const batchUpdateRequest = { requests: requests };
    gapi.client.sheets.spreadsheets
      .batchUpdate({
        spreadsheetId: spreadsheetId,
        resource: batchUpdateRequest,
      })
      .then(() => {
        let urlString =
          `https://docs.google.com/spreadsheets/d/` +
          spreadsheetId +
          `/edit#gid=0`;
        enqueueSnackbar(`Here is the Link to the Spreadsheet: `, {
          variant: "info",
        });
        enqueueSnackbar(<a href={urlString}>{urlString}</a>, {
          variant: "info",
        });
      });
  } catch (err) {
    enqueueSnackbar(err.message, { variant: "error" });
    return;
  }
};

function CreateGoogleSheet({ project }) {
  const [createSheetStarted, setCreateSheetStarted] = useState(false);
  const [createSheetCompleted, setCreateSheetCompleted] = useState(false);

  const [exportEvaOpen, setExportEvaOpen] = useState(false);
  const { enqueueSnackbar } = useSnackbar();

  const handleExportEVAClose = () => {
    setExportEvaOpen(false);
  };

  const handleCloseSnackbar = (event, reason) => {
    if (reason === "clickaway") {
      return;
    }

    setCreateSheetStarted(false);
    setCreateSheetCompleted(false);
  };

  const exportEVA = (period) => {
    setCreateSheetStarted(true);
    CalculateStaticAnalysisWithPeriod(
      project,
      period,
      enqueueSnackbar,
      (result) => {
        let projectAnalysisSpecPer = ProjectAnalysis.fromJS_ToArray(
          result,
          period
        );
        
        createSheets(
          projectAnalysisSpecPer,
          setCreateSheetCompleted,
          period,
          enqueueSnackbar
        );
      }
    );
    setExportEvaOpen(false);
  };

  return (
    <div className="AddGoogleSheetButton">
      
      
      <Tooltip title="ExportEVA" aria-label="Export EVA to GoogleSheets">
        <IconButton onClick={() => setExportEvaOpen(true)} color="inherit">
          <NoteAddIcon />
        </IconButton>
      </Tooltip>
      <DialogExportEVA
        open={exportEvaOpen}
        onCancel={handleExportEVAClose}
        onSave={(period) => loadGoogleClient(() => exportEVA(period))}
      />
      <Snackbar
        title="Create Spreadsheet Start"
        open={createSheetStarted}
        autoHideDuration={3000}
        onClose={handleCloseSnackbar}
      >
        <Alert
          onClose={handleCloseSnackbar}
          severity="info"
          sx={{ width: "100%" }}
        >
          Creating Spreadsheet for Export...
        </Alert>
      </Snackbar>
      <Snackbar
        title="Create Spreadsheet End"
        open={createSheetCompleted}
        autoHideDuration={3000}
        onClose={handleCloseSnackbar}
      >
        <Alert
          onClose={handleCloseSnackbar}
          severity="success"
          sx={{ width: "100%" }}
        >
          Successfully Created the Spreadsheet!!
        </Alert>
      </Snackbar>
    </div>
  );
}

export default CreateGoogleSheet;
